import { isToday as isTodayDateFns } from "date-fns";
import i18next from "i18next";
import moment from "moment-timezone";

export function dateToFromNowDaily(myDate: Date) {
  return moment(myDate).calendar(null, {
    sameDay: "[Today]",
    nextDay: "[Tomorrow]",
    lastDay: "[Yesterday]",
    nextWeek: "dddd",
    lastWeek: "[Last] dddd",
    sameElse: "DD/MM/YYYY",
  });
}

export function isSameDay({ date1, date2 }: { date1: Date; date2: Date }) {
  return moment(date1).isSame(date2, "day");
}

export function getTimezone() {
  return moment.tz.guess();
}

export function getLocalDateAndTimezone(dateUtc: Date | number) {
  return { date: moment(dateUtc).format("YYYY-MM-DDTHH:mm:ss"), timezone: getTimezone() };
}

export function isToday(timestamp: number): boolean {
  return isTodayDateFns(timestamp);
}

export function getCurrentTimeForTimepicker(date: Date | number | string) {
  const now = new Date(date);
  return { hour: now.getHours(), minute: now.getMinutes(), second: now.getSeconds() } as any;
}

export function createDateFromTimepicker({
  hour,
  minute,
  second,
}: {
  hour: number;
  minute: number;
  second: number;
}) {
  const date = new Date();
  date.setHours(hour);
  date.setMinutes(minute);
  date.setSeconds(second);

  return date;
}

export function calculateTimeAgo(createdAt: string) {
  const currentDate = new Date().getTime();
  const createdDate = new Date(createdAt).getTime();

  const diff = currentDate - createdDate;

  const seconds = Math.round(diff / 1000);
  const minutes = Math.round(diff / (1000 * 60));
  const hours = Math.round(diff / (1000 * 60 * 60));
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  const weeks = Math.round(diff / (1000 * 60 * 60 * 24 * 7));
  const months = Math.round(diff / (1000 * 60 * 60 * 24 * 30));
  const years = Math.round(diff / (1000 * 60 * 60 * 24 * 365));

  if (years > 0) {
    return `Posted ${years} year${years > 1 ? "s" : ""} ago`;
  } else if (months > 0) {
    return `Posted ${months} month${months > 1 ? "s" : ""} ago`;
  } else if (weeks > 0) {
    return `Posted ${weeks} week${weeks > 1 ? "s" : ""} ago`;
  } else if (days > 1) {
    return `Posted ${days} days ago`;
  } else if (days === 1) {
    return "Posted one day ago";
  } else if (hours > 0) {
    return `Posted ${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (minutes > 0) {
    return `Posted ${minutes} min${minutes > 1 ? "s" : ""} ago`;
  } else if (seconds >= 0) {
    return "Posted just now";
  }
}

export const formatDate = (inputDateStr) => {
  const inputDate = new Date(inputDateStr);

  if (isNaN(inputDate.getTime())) {
    return "Invalid Date";
  }

  const day = String(inputDate.getDate()).padStart(2, "0");
  const month = String(inputDate.getMonth() + 1).padStart(2, "0");
  const year = inputDate.getFullYear();

  const formattedDate = `${month}/${day}/${year}`;
  return formattedDate;
};

export const getDayWithSuffix = (day) => {
  if (day > 3 && day < 21) return day + "th";
  switch (day % 10) {
    case 1:
      return day + "st";
    case 2:
      return day + "nd";
    case 3:
      return day + "rd";
    default:
      return day + "th";
  }
};

export const formatRemainingTime = (msLeft: number, isCancelled = false, isOnTrial = false) => {
  const MS_IN_A_DAY = 24 * 60 * 60 * 1000;
  const days = Math.floor(msLeft / MS_IN_A_DAY);

  const dayPlural = days > 1 ? i18next.t("subscription.days") : i18next.t("subscription.day");

  if (isOnTrial) {
    return days > 1
      ? i18next.t("subscription.trial_expires_in", { days })
      : i18next.t("subscription.trial_expires_today");
  }

  if (days > 0) {
    return isCancelled
      ? i18next.t("subscription.cancels_in", { days, day_plural: dayPlural })
      : i18next.t("subscription.renews_in", { days, day_plural: dayPlural });
  } else {
    return isCancelled
      ? i18next.t("subscription.cancels_today")
      : i18next.t("subscription.renews_today");
  }
};

export const formatDuration = (milliseconds) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}h ${minutes}m ${seconds}s`;
};
